import type React from "react";
import { useState, useCallback } from "react";
import { Modal, Progress } from "flowbite-react";
import { MdUpload, MdDescription, MdCheckCircle } from "react-icons/md";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { uploadSignedDocument } from "../hooks";

interface DocumentUploadModalProps {
  onClose: () => void;
  equipmentCode: string | undefined;
  employeeName: string | undefined;
  inventoryId: string;
}

export function ModalDocumentUpload({
  onClose,
  equipmentCode,
  employeeName,
  inventoryId,
}: DocumentUploadModalProps) {
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const { notifyError } = useToastContext();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file) {
      notifyError("Selecione um arquivo antes de enviar.");
      return;
    }

    setUploadedFile(file);
    setUploadStep(2);
    setUploadProgress(0);

    let simulatedProgress = 0;
    const interval = setInterval(() => {
      simulatedProgress += Math.random() * 10;
      if (simulatedProgress >= 95) simulatedProgress = 95;
      setUploadProgress(Math.floor(simulatedProgress));
    }, 200);

    try {
      const result = await uploadSignedDocument(inventoryId, file);

      clearInterval(interval);
      setUploadProgress(100);

      if (result.ok) {
        setUploadStep(3);
      } else {
        notifyError(result.message as string);
      }
    } catch (err) {
      clearInterval(interval);
      notifyError("Erro ao enviar o documento.");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const resetModal = () => {
    setUploadStep(1);
    setUploadProgress(0);
    setUploadedFile(null);
    setDragActive(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Modal show={true} onClose={handleClose} popup>
      <Modal.Header className="text-black">
        <h2 className="dark:text-black">Gestão de Documento</h2>
      </Modal.Header>
      <Modal.Body className="text-black">
        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Equipamento:</span>
                <p className="font-medium">{equipmentCode}</p>
              </div>
              <div>
                <span className="text-gray-500">Responsável:</span>
                <p className="font-medium">{employeeName}</p>
              </div>
            </div>
          </div>

          {uploadStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Upload do Documento Assinado</h3>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <MdUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="font-medium mb-2">
                  Arraste o documento assinado aqui
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  ou clique para selecionar o arquivo
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <span className="px-4 py-2 bg-gray-200 rounded">
                    Selecionar Arquivo
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Formatos aceitos: PDF (máx. 10MB)
                </p>
              </div>
            </div>
          )}

          {uploadStep === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Enviando Documento...</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{uploadedFile?.name}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress progress={uploadProgress} size="sm" color="red" />
              </div>
            </div>
          )}

          {uploadStep === 3 && (
            <div className="space-y-4">
              <div className="text-center py-6">
                <MdCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  Upload Realizado com Sucesso!
                </h3>
                <p className="text-gray-500">
                  O documento foi enviado e o processo está completo.
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MdDescription className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium">{uploadedFile?.name}</p>
                      <p className="text-sm text-gray-500">
                        Enviado em {new Date().toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            {uploadStep === 3 && (
              <ButtonWithLoader type="button" onClick={onClose}>
                Finalizar Processo
              </ButtonWithLoader>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
